import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { alpha, Grid, Tab, Tabs, Typography, Stack } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useGetOrganizationCandidateQuery } from 'src/services/candidates/candidates.service';
import { OrganizationCandidateType } from 'src/services/candidates/candidates.types';
import { exportOrgCandidateProfile } from 'src/services/candidates/candidates.utils';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';

import { Box } from '@mui/system';
import CandidateDocumentsView from 'src/sections/candidates/detail/sections/candidate-documents';
import { CandidateDetailSection } from 'src/sections/candidates/detail/sections/detail-section';
import { CandidateContactCard } from 'src/sections/candidates/detail/sections/candidate-contact-card';
import { CandidateLabelsCard } from 'src/sections/candidates/detail/sections/candidate-labels-card';
import { LinkedContactsCard } from 'src/sections/candidates/detail/sections/linked-contacts-card';
import CandidateJobsList from 'src/sections/candidates/jobs/jobs-table-view';
import OrgCandidateProfileImporterDialog from 'src/sections/candidates/modal/candidate-profile-importer';
import AddCandidateJobModal from 'src/sections/jobs/modals/add-candidate-job-modal';
import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';
import ProfileUser from 'src/sections/profile/profile-sections/profile-user';

// ----------------------------------------------------------------------

enum TabValue {
  PROFILE = 'profile',
  JOBS = 'jobs',
  DETAIL = 'detail',
  DOCUMENTS = 'documents',
}

enum ProfileTabValue {
  CRM_PROFILE = 'crm_profile',
  LIVE_PROFILE = 'live_profile'
}

interface Props {
  overrideCandidateId?: string;
}

export default function CandidateDetail({ overrideCandidateId }: Props) {

  const [selectedTab, onSelectTab] = useState<TabValue>();
  const [selectedProfileTab, onSelectProfileTab] = useState<ProfileTabValue>(ProfileTabValue.CRM_PROFILE);

  const { t } = useTranslate();

  const { candidate_id: candidateIdParam } = useParams();

  const dispatch = useAppDispatch();

  const importProfileControl = useBoolean();

  const addCandidateJobControl = useBoolean();

  const [searchParams, setSearchParams] = useSearchParams();

  const candidateId = useMemo(() => {
    if (overrideCandidateId) return overrideCandidateId;

    return candidateIdParam;
  }, [overrideCandidateId, candidateIdParam]);

  const { currentData: orgCandidate, isFetching: isLoading } = useGetOrganizationCandidateQuery(
    candidateId as string,
    {
      skip: !candidateId,
    }
  );

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!selectedTab) return;

    setSearchParams({ "tab": selectedTab }, { replace: true })

  }, [selectedTab, setSearchParams]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: string = TabValue.DETAIL;

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue)) {
      defaultTab = existingTab;
    }

    onSelectTab(defaultTab as TabValue);

  }, [selectedTab, searchParams]);

  const onProfileUpdate = useCallback(() => {
    dispatch(rootApi.util.invalidateTags([{ type: 'OrgCandidates', id: candidateId as string }]));

  }, [dispatch, candidateId]);

  const onImportProfile = useCallback(() => {
    importProfileControl.onTrue();
  }, [importProfileControl]);

  const onProfileImportSuccess = useCallback(() => {
    importProfileControl.onFalse();
  }, [importProfileControl]);

  const openAddCandidateJobModal = useCallback(() => {
    addCandidateJobControl.onTrue();
  }, [addCandidateJobControl]);

  const closeAddCandidateJobModal = useCallback(() => {
    addCandidateJobControl.onFalse();
  }, [addCandidateJobControl]);

  return (
    <Grid container gap={3}>
      <Grid item xs={12}>
        <ProfileUser
          profile={orgCandidate?.candidate_profile}
          isLoading={isLoading}
          mode="view"
          showAvailabilityStatus={orgCandidate?.type !== OrganizationCandidateType.UNLINKED}
          onImportProfile={onImportProfile}
          onAddToJob={!overrideCandidateId ? openAddCandidateJobModal : undefined}
          onExportProfile={!overrideCandidateId ? () => exportOrgCandidateProfile(candidateId as string) : undefined}
        />
      </Grid>

      <Grid item xs={12}>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) => onSelectTab(newValue)}
          sx={{
            px: 2.5,
            mb: { xs: 1, sm: 'unset' },
            boxShadow: (elTheme) => `inset 0 -2px 0 0 ${alpha(elTheme.palette.grey[500], 0.08)}`,
          }}
        >
          <Tab
            key={TabValue.DETAIL}
            iconPosition="end"
            value={TabValue.DETAIL}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.detail')}
              </Typography>
            }
          />

          <Tab
            key={TabValue.PROFILE}
            iconPosition="end"
            value={TabValue.PROFILE}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.profile')}
              </Typography>
            }
          />
          <Tab
            key={TabValue.JOBS}
            iconPosition="end"
            value={TabValue.JOBS}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.jobs')}
              </Typography>
            }
          />

          <Tab
            key={TabValue.DOCUMENTS}
            iconPosition="end"
            value={TabValue.DOCUMENTS}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.documents')}
              </Typography>
            }
          />

        </Tabs>
      </Grid>


      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {selectedTab === TabValue.DETAIL && (
            <CandidateDetailSection candidateId={candidateId as string} />
          )}

          {selectedTab === 'profile' && (
            <>
              {
                orgCandidate?.type === OrganizationCandidateType.LINKED && (
                  <Box mb={2}>
                    <Tabs
                      value={selectedProfileTab}
                      onChange={(event, newValue) => onSelectProfileTab(newValue)}
                      sx={{
                        px: 2.5,
                        mb: { xs: 1, sm: 'unset' },
                        boxShadow: (elTheme) => `inset 0 -2px 0 0 ${alpha(elTheme.palette.grey[500], 0.08)}`,
                      }}
                    >
                      <Tab
                        key={ProfileTabValue.CRM_PROFILE}
                        iconPosition="end"
                        value={ProfileTabValue.CRM_PROFILE}
                        label={
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                          >
                            {t('candidates.detail.tabs.crm_profile')}
                          </Typography>
                        }
                      />

                      <Tab
                        key={ProfileTabValue.LIVE_PROFILE}
                        iconPosition="end"
                        value={ProfileTabValue.LIVE_PROFILE}
                        label={
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                          >
                            {t('candidates.detail.tabs.live_profile')}
                          </Typography>
                        }
                      />
                    </Tabs>
                  </Box>
                )
              }

              {
                selectedProfileTab === ProfileTabValue.CRM_PROFILE && (
                  <CandidateProfileEditor
                    onUpdate={onProfileUpdate}
                    profile={orgCandidate?.candidate_profile}
                    isLoading={isLoading}
                    mode="edit"
                  />
                )
              }

              {
                selectedProfileTab === ProfileTabValue.LIVE_PROFILE && (
                  <CandidateProfileEditor
                    onUpdate={() => { }}
                    profile={orgCandidate?.synced_profile}
                    isLoading={isLoading}
                    mode="view"
                  />
                )
              }
            </>
          )}

          {selectedTab === 'jobs' && (
            <CandidateJobsList candidateId={candidateId as string} />
          )}

          {selectedTab === 'documents' && (
            <CandidateDocumentsView candidateId={candidateId as string} />
          )}
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Stack direction="column" spacing={2}>
            {/* Candidate's contact information */}
            <CandidateContactCard candidateId={candidateId as string} />

            {/* Candidate's linked contacts */}
            {orgCandidate && (
              <>
                <LinkedContactsCard candidateId={orgCandidate.id} />
                <CandidateLabelsCard candidateId={orgCandidate.id} />
              </>
            )}
          </Stack>
        </Grid>
      </Grid>

      {importProfileControl.value && (
        <OrgCandidateProfileImporterDialog
          candidateId={candidateId as string}
          open={importProfileControl.value}
          onClose={importProfileControl.onFalse}
          onSuccess={onProfileImportSuccess}
        />
      )}

      <AddCandidateJobModal open={addCandidateJobControl.value} onClose={closeAddCandidateJobModal} candidateId={candidateId as string} />
    </Grid>
  );
}
